'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetSessionsResponse } from '@shared/all/domains/sessions';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useAuth from '@shared/utils/hooks/useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { client, service } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalHeader } from '../HOC/Modal';
import RunnerCliTokens from './RunnerCliTokens';
import SessionItem from './SessionItem';
import SessionLogoutConfirmation from './SessionLogoutCOnfirmation';
const Sessions = () => {
  const $ = _c(37);
  const router = useRouter();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      client,
      service
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    logout
  } = useAuth(t0);
  const queryKey = ["sessions"];
  const {
    data,
    isLoading,
    isError
  } = useQuery({
    queryKey,
    queryFn: _temp
  });
  const [endAllSessionsModalOpen, setEndAllSessionsModalOpen] = useState(false);
  const removeSessionFromState = id => function () {
    queryClient.setQueryData(queryKey, oldData => ({
      ...oldData,
      sessions: oldData?.sessions.filter(session => session.id !== id) || []
    }));
  };
  let t1;
  if ($[1] !== logout || $[2] !== router) {
    t1 = async () => {
      const [error] = await client.sessions.endAll.mutate({});
      if (error) {
        return;
      }
      logout();
      toast("Ended all sessions. Please login again.");
      router.push("/");
    };
    $[1] = logout;
    $[2] = router;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const deleteAllSessions = t1;
  const t2 = "p-6";
  let t3;
  if ($[4] !== data) {
    t3 = data && data.sessions.length > 0 && <h2 className="text-2xl font-semibold">You're signed in to {data.sessions.length} session{data.sessions.length === 1 ? "" : "s"}</h2>;
    $[4] = data;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== data?.sessions.length) {
    t4 = data?.sessions.length === 0 && <h2 className="text-2xl font-semibold">Managing your sessions</h2>;
    $[6] = data?.sessions.length;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="text-textSecondary/70">Sessions can sometimes take up to 30 minutes to be updated.</p>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4) {
    t6 = <div className="space-y-2">{t3}{t4}{t5}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== setEndAllSessionsModalOpen) {
    t7 = () => setEndAllSessionsModalOpen(true);
    $[12] = setEndAllSessionsModalOpen;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = data?.sessions.length === 0;
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <Button text="End all sessions" onClick={t7} disabled={t8} />;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <div className="flex flex-col items-center space-y-2 lg:items-start">{t6}{t9}</div>;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = "grid grid-cols-1 items-center md:grid-cols-2 md:gap-x-2";
  const t12 = !isLoading && !isError && data?.sessions.map(session_0 => <SessionItem key={session_0.id} session={session_0} removeSession={removeSessionFromState(session_0.id)} isLast={Boolean(session_0.device) && data.sessions.filter(_temp2).length === 1} />);
  let t13;
  if ($[20] !== isLoading) {
    t13 = isLoading && <><PlaceholderSessionItem /><PlaceholderSessionItem /><PlaceholderSessionItem /></>;
    $[20] = isLoading;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t12 || $[23] !== t13) {
    t14 = <div className={t11}>{t12}{t13}</div>;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== isError) {
    t15 = isError && <p className="text-center text-textSecondary/70">An unknown error occured. Please try again later after some time.</p>;
    $[25] = isError;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <RunnerCliTokens />;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== deleteAllSessions || $[29] !== endAllSessionsModalOpen || $[30] !== setEndAllSessionsModalOpen) {
    t17 = endAllSessionsModalOpen && <Modal closeHandler={() => setEndAllSessionsModalOpen(false)}><ModalHeader closeHandler={() => setEndAllSessionsModalOpen(false)}><h2>Manage sessions</h2></ModalHeader><SessionLogoutConfirmation logoutHandler={deleteAllSessions} allSessions={true} isLast={false} /></Modal>;
    $[28] = deleteAllSessions;
    $[29] = endAllSessionsModalOpen;
    $[30] = setEndAllSessionsModalOpen;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t10 || $[33] !== t14 || $[34] !== t15 || $[35] !== t17) {
    t18 = <div className={t2}>{t10}{t14}{t15}{t16}{t17}</div>;
    $[32] = t10;
    $[33] = t14;
    $[34] = t15;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
};
function PlaceholderSessionItem() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className=""><div><p className="flex items-center space-x-2"><Globe size={20} /><span>Loading...</span></p><p>Loading...</p></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export default Sessions;
function _temp() {
  return unwrapFromResult(client.sessions.get.query());
}
function _temp2(d) {
  return d.device;
}